import {
  LearnerClassroomAssignmentClassPostsBySectionQuery,
  LearnerClassroomAssignmentClassPostsBySectionQueryVariables,
  LearnerCommentOnAssignmentMutation,
  LearnerCommentOnAssignmentMutationVariables,
  LearnerSingleAssignmentPostQuery,
  LearnerSingleAssignmentPostQueryVariables,
} from "@outschool/gql-frontend-generated";
import {
  ApolloCache,
  FetchResult,
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import {
  AssignmentSubmissionThread,
  AssignmentSummaryCard,
  EditableMessageContentSubmitType,
  MessageContentFragment,
} from "@outschool/ui-components-classroom";
import { addTypeName } from "@outschool/ui-utils";

import {
  CurrentLearner,
  useCurrentLearner,
} from "../providers/CurrentLearnerProvider";
import { singleAssignmentPostQuery } from "./LearnerSingleAssignmentPostQuery";

export const classroomAssignmentsQuery = gql`
  query LearnerClassroomAssignmentClassPostsBySection($sectionUid: ID!) {
    section(uid: $sectionUid) {
      uid
      assignmentClassPosts {
        uid
        user {
          uid
          name
        }
        title
        messageContent {
          ...MessageContentFragment
        }
        submissionRequirement
        publishAt
        dueDate
        comments {
          ...AssignmentCommentThread
        }
        ...AssignmentSummaryCardAssignment
      }
    }
  }
  ${MessageContentFragment}
  ${AssignmentSubmissionThread.fragments.assignmentCommentThread}
  ${AssignmentSummaryCard.fragments.assignmentSummary}
`;

export const useLearnerClassroomAssignmentsBySectionQuery = (
  variables: LearnerClassroomAssignmentClassPostsBySectionQueryVariables,
  options?: { skip?: boolean },
) => {
  return useQueryWithPreviousData<
    LearnerClassroomAssignmentClassPostsBySectionQuery,
    LearnerClassroomAssignmentClassPostsBySectionQueryVariables
  >(classroomAssignmentsQuery, {
    variables,
    skip: options?.skip,
  });
};

const learnerCommentOnAssignmentMutation = gql`
  mutation LearnerCommentOnAssignment(
    $assignmentClassPostUid: ID!
    $messageContentInput: MessageContentInput!
  ) {
    addAssignmentClassPostComment(
      assignmentClassPostUid: $assignmentClassPostUid
      messageContent: $messageContentInput
    ) {
      uid
      sentAt
      isSubmission
      messageContent {
        ...MessageContentFragment
      }
    }
  }
  ${MessageContentFragment}
`;

export const useLearnerCommentOnAssignmentMutation = ({
  sectionUid,
}: {
  sectionUid: string;
}) => {
  const currentLearner = useCurrentLearner()!;

  const [submitComment, { error, loading }] = useMutation<
    LearnerCommentOnAssignmentMutation,
    LearnerCommentOnAssignmentMutationVariables
  >(learnerCommentOnAssignmentMutation);

  return {
    submitComment: async (input: {
      assignmentClassPostUid: string;
      messageContentInput: EditableMessageContentSubmitType;
    }) => {
      const { assignmentClassPostUid, messageContentInput } = input;
      const { mentions, attachments, text, textMimeType, video } =
        messageContentInput;
      const mentionsUids = mentions?.map(m => m.id) || [];
      const attachmentUids = attachments?.map(a => a.uid) || [];

      const variables: LearnerCommentOnAssignmentMutationVariables = {
        assignmentClassPostUid,
        messageContentInput: {
          text,
          textMimeType,
          mentionsUids,
          attachmentUids,
          videoUid: video?.uid,
        },
      };

      const result = await submitComment({
        variables,
        update: learnerCommentSubmitCacheUpdate({
          sectionUid,
          assignmentClassPostUid,
          currentLearner,
        }),
      });

      if (result.errors) {
        return false;
      }
      return true;
    },
    error,
    loading,
  };
};

interface LearnerAssignmentCommentThreadShape {
  uid: string;
  sender: {
    uid: string;
  };
  replies: Array<{
    uid: string;
  }> | null;
}

const upsertLearnerAssignmentCommentThread = <
  TComment extends LearnerAssignmentCommentThreadShape,
>({
  comments,
  currentLearner,
  newReply,
}: {
  comments: TComment[];
  currentLearner: CurrentLearner;
  newReply: LearnerCommentOnAssignmentMutation["addAssignmentClassPostComment"];
}) => {
  const sender = addTypeName("MessageSender", {
    uid: currentLearner.uid,
    name: currentLearner.name,
    photo: null,
    avatar: currentLearner.avatar,
    isLearner: true,
  });

  const existingComment = comments.find(
    comment => comment.sender.uid === currentLearner.uid,
  );

  if (!existingComment) {
    // Idempotency guard: skip if this mutation result was already applied.
    if (comments.some(comment => comment.uid === newReply.uid)) {
      return comments;
    }

    const newComment = addTypeName("AssignmentClassPostComment", {
      ...newReply,
      // first comment is always the submission
      isSubmission: true,
      threadForLearnerUid: currentLearner.uid,
      sender,
      replies: [],
    }) as unknown as TComment;
    return [newComment];
  }

  // Idempotency guard: if this is the same node as the existing parent comment, do not append a reply.
  if (existingComment.uid === newReply.uid) {
    return comments;
  }

  const newThreadReply = addTypeName("AssignmentClassPostComment", {
    ...newReply,
    isSubmission: false,
    threadForLearnerUid: currentLearner.uid,
    sender,
    replies: [],
  });

  // Idempotency guard: the same network result can be seen via multiple cache writes.
  if (
    (existingComment.replies || []).some(reply => reply.uid === newReply.uid)
  ) {
    return comments;
  }

  return comments.map(comment =>
    comment.uid === existingComment.uid
      ? {
          ...comment,
          // placing new reply at end of thread
          replies: [...(comment.replies || []), newThreadReply],
        }
      : comment,
  );
};

const learnerCommentSubmitCacheUpdate =
  ({
    sectionUid,
    assignmentClassPostUid,
    currentLearner,
  }: {
    sectionUid: string;
    assignmentClassPostUid: string;
    currentLearner: CurrentLearner;
  }) =>
  (
    cache: ApolloCache<any>,
    { data }: Omit<FetchResult<LearnerCommentOnAssignmentMutation>, "context">,
  ) => {
    if (!data?.addAssignmentClassPostComment) {
      return;
    }

    const newReply = data.addAssignmentClassPostComment;
    const classroomCacheData = cache.readQuery<
      LearnerClassroomAssignmentClassPostsBySectionQuery,
      LearnerClassroomAssignmentClassPostsBySectionQueryVariables
    >({
      query: classroomAssignmentsQuery,
      variables: { sectionUid },
    });

    if (classroomCacheData?.section) {
      const { section } = classroomCacheData;
      const { assignmentClassPosts } = section;
      const assignmentClassPost = assignmentClassPosts?.find(
        ({ uid }) => uid === assignmentClassPostUid,
      );

      if (assignmentClassPost) {
        const updatedAssignmentClassPost = {
          ...assignmentClassPost,
          comments: upsertLearnerAssignmentCommentThread({
            comments: assignmentClassPost.comments,
            currentLearner,
            newReply,
          }),
        };

        cache.writeQuery({
          query: classroomAssignmentsQuery,
          variables: { sectionUid },
          data: {
            section: {
              ...section,
              assignmentClassPosts: assignmentClassPosts?.map(existingPost =>
                existingPost.uid === assignmentClassPostUid
                  ? updatedAssignmentClassPost
                  : existingPost,
              ),
            },
          },
        });
      }
    }

    const singleAssignmentCacheData = cache.readQuery<
      LearnerSingleAssignmentPostQuery,
      LearnerSingleAssignmentPostQueryVariables
    >({
      query: singleAssignmentPostQuery,
      variables: { assignmentClassPostUid },
    });

    if (singleAssignmentCacheData?.assignmentClassPost) {
      const { assignmentClassPost } = singleAssignmentCacheData;

      const updatedSingleAssignmentClassPost = {
        ...assignmentClassPost,
        comments: upsertLearnerAssignmentCommentThread({
          comments: assignmentClassPost.comments,
          currentLearner,
          newReply,
        }),
      };

      cache.writeQuery({
        query: singleAssignmentPostQuery,
        variables: { assignmentClassPostUid },
        data: {
          assignmentClassPost: updatedSingleAssignmentClassPost,
        },
      });
    }
  };
